import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { getApiError } from '@/api/helpers/apiError'
import { useRefreshCameraImage } from '@/queries/cameraQueries'
import { usePollTaskStatus } from '@/queries/generalQueries'
import { showToast } from '@/theme/notifications'

type UseCameraRefreshParams = {
  deviceId?: string
  cameraId?: string
  onRefresh?: () => void
}

export const useCameraRefresh = ({
  deviceId,
  cameraId,
  onRefresh
}: UseCameraRefreshParams) => {
  const intl = useIntl()

  const [refreshImageTaskId, setRefreshImageTaskId] = useState<string>('')

  const { mutateAsync: refreshImage, isPending: isPendingImageRefresh } =
    useRefreshCameraImage()

  const { data: refreshImageTaskData, isLoading: isLoadingTaskStatus } =
    usePollTaskStatus(refreshImageTaskId, refreshImageTaskId !== '')

  useEffect(() => {
    if (refreshImageTaskData?.task_status === 'SUCCESS') {
      setRefreshImageTaskId('')
      onRefresh?.()
      showToast(
        intl.formatMessage({ id: 'cameras.image.refresh.success' }),
        'success'
      )
    }

    if (refreshImageTaskData?.task_status === 'FAILURE') {
      setRefreshImageTaskId('')
      showToast(
        intl.formatMessage({ id: 'cameras.image.refresh.error' }),
        'error'
      )
    }
  }, [refreshImageTaskData, onRefresh, intl])

  const isRefreshing =
    isPendingImageRefresh ||
    isLoadingTaskStatus ||
    refreshImageTaskData?.task_status === 'PENDING'

  const handleRefresh = useCallback(async () => {
    if (!deviceId || !cameraId) {
      return
    }

    try {
      const { task_id } = await refreshImage({ deviceId, cameraId })
      setRefreshImageTaskId(task_id)
    } catch (err) {
      const { errorMessage } = getApiError(err)

      const message =
        errorMessage ||
        intl.formatMessage({ id: 'cameras.image.refresh.error' })

      showToast(message, 'error')
    }
  }, [refreshImage, deviceId, cameraId, intl])

  return useMemo(
    () => ({
      isRefreshing,
      refreshCameraImage: handleRefresh
    }),
    [isRefreshing, handleRefresh]
  )
}
